import axios from "axios";
import { APP_WEBHOOKS_API_BASE } from "../../../../config/api";

export const WEBHOOKS_API = APP_WEBHOOKS_API_BASE;

/** Firebase session token (UI) or whk_* webhook API key (automation). */
export function buildAuthHeaders(token, activeOrg) {
  return {
    Authorization: `Bearer ${token}`,
    ...(activeOrg?.id ? { "X-Org-ID": activeOrg.id } : {}),
  };
}

export async function getRequest(url, token, activeOrg, config = {}) {
  const res = await axios.get(url, {
    ...config,
    headers: { ...buildAuthHeaders(token, activeOrg), ...(config.headers || {}) },
  });
  return res.data;
}

export async function postRequest(url, body, token, activeOrg, config = {}) {
  const res = await axios.post(url, body, {
    ...config,
    headers: { ...buildAuthHeaders(token, activeOrg), ...(config.headers || {}) },
  });
  return res.data;
}

export async function patchRequest(url, body, token, activeOrg, config = {}) {
  const res = await axios.patch(url, body, {
    ...config,
    headers: { ...buildAuthHeaders(token, activeOrg), ...(config.headers || {}) },
  });
  return res.data;
}

export async function deleteRequest(url, token, activeOrg, config = {}) {
  const res = await axios.delete(url, {
    ...config,
    headers: { ...buildAuthHeaders(token, activeOrg), ...(config.headers || {}) },
  });
  return res.data;
}

export function getErrorMessage(error, fallback = "Request failed") {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    fallback
  );
}

export const DEFAULT_WEBHOOK_TEMPLATE = `{
  "event_id": "{{ event.id }}",
  "event_type": "{{ event.type }}",
  "occurred_at": "{{ event.occurred_at }}",
  "organization_id": "{{ event.organization_id }}",
  "client_organization": {{ client_organization | tojson }},
  "resource": {
    "type": "{{ resource.type }}",
    "id": "{{ resource.id }}",
    "attributes": {{ resource.attributes | tojson }}
  }
}`;

/** All sensor keys from device payload (any field names) */
export const TELEMETRY_WEBHOOK_TEMPLATE = `{
  "event_type": "{{ event.type }}",
  "device_id": "{{ resource.id }}",
  "timestamp": "{{ resource.attributes['timestamp'] }}",
  "packet_type": "{{ resource.attributes['packet_type'] }}",
  "readings": {{ resource.attributes['data'] | tojson }}
}`;

/** Example: pick only the fields you want — rename keys to match your device JSON */
export const TELEMETRY_CUSTOM_FIELDS_TEMPLATE = `{
  "event_type": "{{ event.type }}",
  "device_id": "{{ resource.id }}",
  "timestamp": "{{ resource.attributes['timestamp'] }}",
  "battery": {{ resource.attributes['data']['Battery'] }},
  "moisture": {{ resource.attributes['data']['Moisture'] }}
}`;

export const TELEMETRY_BATCH_WEBHOOK_TEMPLATE = `{
  "event_type": "{{ event.type }}",
  "batch_id": "{{ resource.id }}",
  "window": {{ resource.attributes['window'] | tojson }},
  "record_count": {{ resource.attributes['record_count'] }},
  "records": {{ resource.attributes['records'] | tojson }}
}`;

export const TELEMETRY_BATCH_CUSTOM_FIELDS_TEMPLATE = `{
  "event_type": "{{ event.type }}",
  "batch_id": "{{ resource.id }}",
  "window": {{ resource.attributes['window'] | tojson }},
  "record_count": {{ resource.attributes['record_count'] }},
  "records": [
    {% for record in resource.attributes['records'] %}
    {
      "device_id": "{{ record['device_id'] }}",
      "device_type_id": "{{ record['device_type_id'] }}",
      "timestamp": "{{ record['timestamp'] }}",
      "battery": {% if "Battery" in record['data'] %}{{ record['data']['Battery'] }}{% else %}null{% endif %},
      "moisture": {% if "Moisture" in record['data'] %}{{ record['data']['Moisture'] }}{% else %}null{% endif %}
    }{% if not loop.last %},{% endif %}
    {% endfor %}
  ]
}`;

/** Mixed device types: optional fields (pressure vs soil, etc.) */
/** Common device JSON keys — user can type any custom name */
export const SENSOR_FIELD_SUGGESTIONS = [
  "Battery",
  "Moisture",
  "Temperature",
  "Humidity",
  "Pressure",
  "Nitrogen",
  "Phosphorus",
  "Potassium",
  "EC",
  "pH",
  "R",
  "Y",
  "B",
];

export function buildTelemetryTemplateFromFields(fields) {
  const valid = (fields || [])
    .map((f) => String(f || "").trim())
    .filter(Boolean);
  if (!valid.length) {
    throw new Error("Select at least one sensor field");
  }
  const lines = [
    "{",
    '  "event_type": "{{ event.type }}",',
    '  "device_id": "{{ resource.id }}",',
    '  "timestamp": "{{ resource.attributes[\'timestamp\'] }}",',
  ];
  valid.forEach((field, idx) => {
    const q = field.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
    const outKey = field.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    const comma = idx < valid.length - 1 ? "," : "";
    lines.push(
      `  "${outKey}": {{ resource.attributes['data']['${q}'] }}${comma}`,
    );
  });
  lines.push("}");
  return lines.join("\n");
}

export function buildTelemetryBatchTemplateFromFields(fields) {
  const valid = (fields || [])
    .map((f) => String(f || "").trim())
    .filter(Boolean);
  if (!valid.length) {
    throw new Error("Select at least one sensor field");
  }
  const lines = [
    "{",
    '  "event_type": "{{ event.type }}",',
    '  "batch_id": "{{ resource.id }}",',
    '  "window": {{ resource.attributes[\'window\'] | tojson }},',
    '  "record_count": {{ resource.attributes[\'record_count\'] }},',
    '  "records": [',
    "    {% for record in resource.attributes['records'] %}",
    "    {",
    '      "device_id": "{{ record[\'device_id\'] }}",',
    '      "device_type_id": "{{ record[\'device_type_id\'] }}",',
    '      "timestamp": "{{ record[\'timestamp\'] }}",',
  ];
  valid.forEach((field, idx) => {
    const q = field.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
    const outKey = field.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    const comma = idx < valid.length - 1 ? "," : "";
    lines.push(
      `      "${outKey}": {% if "${q}" in record['data'] %}{{ record['data']['${q}'] }}{% else %}null{% endif %}${comma}`,
    );
  });
  lines.push("    }{% if not loop.last %},{% endif %}");
  lines.push("    {% endfor %}");
  lines.push("  ]");
  lines.push("}");
  return lines.join("\n");
}

export const TELEMETRY_FLEXIBLE_SENSORS_TEMPLATE = `{
  "event_type": "{{ event.type }}",
  "device_id": "{{ resource.id }}",
  "timestamp": "{{ resource.attributes['timestamp'] }}",
  "battery": {% if "Battery" in resource.attributes['data'] %}{{ resource.attributes['data']['Battery'] }}{% else %}null{% endif %},
  "moisture": {% if "Moisture" in resource.attributes['data'] %}{{ resource.attributes['data']['Moisture'] }}{% else %}null{% endif %},
  "pressure": {% if "Pressure" in resource.attributes['data'] %}{{ resource.attributes['data']['Pressure'] }}{% else %}null{% endif %},
  "temperature": {% if "Temperature" in resource.attributes['data'] %}{{ resource.attributes['data']['Temperature'] }}{% else %}null{% endif %}
}`;
